import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import classNames from 'clsx';

import { DESIGN_INLINE, DESIGN_INLINE_VERTICAL, useChannelConfig } from '../../../constants/carousels';

import type { ChannelType } from '@/modules/common/types/channel';
import type { VideoType } from '@/modules/common/types/video';

import { isVisibleInConfig } from '@/modules/common/helpers/configuration';
import { useSmallScreen } from '@/modules/common/helpers/customHooks';
import { getFileUrlByDimensions } from '@/modules/common/helpers/file';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';

import Image from '../../../../common/components/Image/Image';
import VideoInfo from '../../../../common/components/VideoInfo/VideoInfo';
import VideoThumbnail from '../../../../common/components/VideoThumbnail/VideoThumbnail';
import SlideSkeleton from './SlideSkeleton';
import { Box, Link, Typography } from '@/mui/components';

import styles from './Slide.module.scss';

type Props = {
  showContent: boolean;
  isActive: boolean;
  lazyLoading: boolean;
  channel: ChannelType;
  video: VideoType;
  width: number;
  height: number;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, mediaId: VideoType['mediaid']) => void;
  onDoubleClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, mediaId: VideoType['mediaid']) => void;
  activeCustomContent: React.ReactNode;
};

function Slide({
  isActive = false,
  showContent = true,
  lazyLoading = false,
  className = '',
  channel,
  activeCustomContent,
  onClick,
  onDoubleClick,
  video,
  width,
  height,
}: Props) {
  const location = useLocation();
  const isSmallScreen = useSmallScreen();
  const [isHovered, setHoverState] = useState(false);
  const configByChannel = useChannelConfig();

  const videoCardConfig = configByChannel.videoCard;
  const isVideoCardStatic = videoCardConfig.static ?? false;
  const videoCardTitleConfig = videoCardConfig.title;
  const slideActiveColor = videoCardConfig.activeColor;

  const isInlineTypeSlide = [DESIGN_INLINE, DESIGN_INLINE_VERTICAL].includes(channel.design);

  let videoInfoRows = isSmallScreen ? 1 : 3;

  if (isVideoCardStatic) {
    videoInfoRows = isSmallScreen ? 1 : 2;
  }

  const showVideoInfo = !isInlineTypeSlide
    ? isVisibleInConfig(videoCardConfig.visible)
    : videoCardConfig.visible === true;

  return (
    <Link
      component={RouterLink}
      to={`/channel/${channel.id}/${video.mediaid}${location.search}`}
      style={{
        width,
      }}
      color="inherit"
      className={classNames(styles.Slide, className)}
      onClick={(event) => {
        if (onClick) {
          onClick(event, video.mediaid);
        }
      }}
      onDoubleClick={(event) => {
        if (onDoubleClick) {
          onDoubleClick(event, video.mediaid);
        }
      }}
      onMouseEnter={() => setHoverState(true)}
      onMouseLeave={() => setHoverState(false)}
    >
      <div
        className={styles.SlideContainer}
        style={{
          width,
          height,
        }}
      >
        {showContent && (
          <>
            {isInlineTypeSlide && (
              <Box
                style={{
                  borderColor: slideActiveColor,
                }}
                className={classNames(styles.Highlight, {
                  [styles.Highlight___default]: !slideActiveColor,
                  [styles.Highlight___active]: isActive,
                })}
              />
            )}
            <Image
              src={getFileUrlByDimensions(video.images, width, height)}
              cover
              width={width}
              height={height}
              lazyLoading={lazyLoading}
            />
            {isActive && activeCustomContent ? (
              activeCustomContent
            ) : (
              <>
                {!isMobileUserAgent && (
                  <>
                    {!isInlineTypeSlide && !isMobileUserAgent && (
                      <VideoThumbnail
                        className={styles.VideoThumbnail}
                        previewUrl={video.previewUrl}
                        isHovered={isHovered}
                      />
                    )}
                    {showVideoInfo && (
                      <VideoInfo
                        title={video.title}
                        description={video.plot}
                        date={video.created}
                        duration={video.duration}
                        staticType={isVideoCardStatic}
                        isHovered={isVideoCardStatic || isHovered}
                        rows={videoInfoRows}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
      {!isVideoCardStatic && isVisibleInConfig(videoCardTitleConfig.visible) && (
        <Typography
          title={video.title}
          data-a="video-title"
          style={{
            fontFamily: videoCardTitleConfig.fontFamily,
          }}
          className={classNames(styles.SlideTitle, {
            [styles.SlideTitle___default]: !videoCardTitleConfig.typography,
          })}
          color={videoCardTitleConfig.color}
          variant={videoCardTitleConfig.typography ?? 'inherit'}
          fontWeight={videoCardTitleConfig.fontWeight ?? 400}
          rows={1}
        >
          {video.title}
        </Typography>
      )}
    </Link>
  );
}

export const Skeleton = SlideSkeleton;

export default Slide;
